import React from 'react';
import Typography from '../Typography/Typography';
import styles from './DaysList.module.css';

/**
 * @typedef {Object} DaysListProps
 * @property {string[]} days - Array of day keys (e.g., ['Sunday', 'Monday', ...])
 * @property {string[]} [activeDays] - Array of currently selected day keys (legacy alias for value)
 * @property {string[]} [value] - Array of currently selected day keys
 * @property {(day: string) => void} [action] - Callback function when a day is clicked (keeping for legacy support)
 * @property {(day: string) => void} [onChange] - Callback for when the days change
 * @property {boolean} [disablePointer=false] - If true, pointer events are disabled
 * @property {boolean} [white=true] - If true, uses the white theme version
 * @property {Object.<string, string>} [dayLabels] - Custom labels for days (e.g., { Monday: 'Mo' })
 * @property {string} [className] - Optional custom class for the container
 */

/**
 * A component to display and select days of the week.
 * 
 * @param {DaysListProps} props
 * @returns {React.ReactElement}
 */
const DaysList = ({
  days = [],
  activeDays = [],
  value,
  action,
  onChange,
  disablePointer = false,
  white = true,
  dayLabels = {},
  className = '',
}) => {
  const selectedDays = value || activeDays;
  const defaultLabels = {
    Sunday: 'Sun',
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat'
  };

  const labels = { ...defaultLabels, ...dayLabels };

  return (
    <div className={`${styles.container} ${className} days-list-container`.trim()}>
      {days.map((day, index) => {
        const isSelected = selectedDays.indexOf(day) > -1;

        const dayClasses = [
          styles.dayStyle,
          'days-list-item',
          disablePointer && styles.disableClickPointer,
          !white && styles.notWhite,
          isSelected && (white ? styles.activeDayStyle : styles.notWhiteActiveDayStyle),
          isSelected && 'days-list-item-active'
        ].filter(Boolean).join(' ');

        return (
          <div
            className={dayClasses}
            key={index}
            onClick={() => {
              if (!disablePointer) {
                onChange?.(day);
                action?.(day);
              }
            }}
          >
            <Typography component="span" className="days-list-label">
              {labels[day] || day}
            </Typography>
          </div>
        );
      })}
    </div>
  );
};

export default DaysList;
